import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { CommunicationsStatCard } from '@/modules/communications/components/CommunicationsStatCard';
import {
  MessageSquare,
  Send,
  Mail,
  Phone,
  CheckCircle2,
  XCircle,
  Clock,
  TrendingUp,
  ArrowRight,
  Settings,
  FileText,
  Bell,
  RefreshCw } from
'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend } from
'recharts';

const API_URL = API_BASE;

const channelIcons = {
  Email: Mail,
  WhatsApp: MessageSquare,
  SMS: Phone,
  Telegram: Send
};

const channelColors = {
  Email: '#8b5cf6',
  WhatsApp: '#22c55e',
  SMS: '#f97316',
  Telegram: '#3b82f6'
};

export const CommunicationsDashboard = () => {const { t } = useTranslation("comms");
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/communications/dashboard`);
      if (!response.ok) throw new Error('Failed to fetch dashboard');
      const data = await response.json();
      setDashboardData(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      // Fallback to empty state
      setDashboardData({
        stats: { total_sent: 0, delivered: 0, failed: 0, pending: 0 },
        channels: [],
        messages_by_channel: [],
        messages_by_day: [],
        recent_logs: []
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-500" />
      </div>);

  }

  const stats = dashboardData?.stats || { total_sent: 0, delivered: 0, failed: 0, pending: 0 };
  const messagesByChannel = dashboardData?.messages_by_channel || [];
  const messagesByDay = dashboardData?.messages_by_day || [];
  const recentLogs = dashboardData?.recent_logs || [];

  return (
    <div className="space-y-6" data-testid="communications-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("communicationsHub", "Communications Hub")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("unifiedMessagingAcross", "Unified messaging across all channels")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchDashboard} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Link to="/communications/templates">
            <Button variant="outline" className="gap-2">
              <FileText className="h-4 w-4" />{t("templates", "Templates")}

            </Button>
          </Link>
          <Link to="/communications/rules">
            <Button className="gap-2 bg-violet-600 hover:bg-violet-700">
              <Bell className="h-4 w-4" />{t("notificationRules", "Notification Rules")}

            </Button>
          </Link>
        </div>
      </div>

      {error &&
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
          <p className="text-yellow-500 text-sm">{t("usingCachedData", "Using cached data -")}{error}</p>
        </div>
      }

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <CommunicationsStatCard
          title="Total Sent"
          value={stats.total_sent}
          icon={Send}
          subtitle={`+${Math.round(stats.total_sent * 0.12)} this week`}
          color="violet" />
        
        <CommunicationsStatCard
          title="Delivered"
          value={stats.delivered}
          icon={CheckCircle2}
          subtitle={`${(stats.delivered / Math.max(stats.total_sent, 1) * 100).toFixed(1)}% rate`}
          color="green" />
        
        <CommunicationsStatCard
          title="Failed"
          value={stats.failed}
          icon={XCircle}
          subtitle={`${(stats.failed / Math.max(stats.total_sent, 1) * 100).toFixed(1)}% rate`}
          color="red" />
        
        <CommunicationsStatCard
          title="Pending"
          value={stats.pending}
          icon={Clock}
          subtitle="Processing..."
          color="amber" />
        
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Messages by Channel */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">{t("messagesByChannel", "Messages by Channel")}</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={messagesByChannel}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="name" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }} />
                
                <Bar dataKey="sent" fill="#8b5cf6" name="Sent" radius={[4, 4, 0, 0]} />
                <Bar dataKey="delivered" fill="#22c55e" name="Delivered" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Messages Over Time */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">{t("messagesOverTime", "Messages Over Time")}</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={messagesByDay}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="date" stroke="#9ca3af" />
                <YAxis stroke="#9ca3af" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }} />
                
                <Area
                  type="monotone"
                  dataKey="sent"
                  stroke="#8b5cf6"
                  fill="#8b5cf6"
                  fillOpacity={0.3}
                  name="Messages Sent" />
                
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold">{t("recentActivity", "Recent Activity")}</CardTitle>
          <Link to="/communications/logs">
            <Button variant="ghost" size="sm" className="gap-2">{t("viewAll", "View All")}

              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {recentLogs.length === 0 ?
            <p className="text-center text-muted-foreground py-8">{t("noRecentMessages", "No recent messages")}</p> :

            recentLogs.slice(0, 5).map((log, index) => {
              const ChannelIcon = channelIcons[log.channel?.charAt(0).toUpperCase() + log.channel?.slice(1)] || MessageSquare;
              return (
                <div
                  key={log.id || index}
                  className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 hover:bg-slate-800 transition-colors">
                  
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${
                    log.status === 'delivered' ? 'bg-green-500/20' :
                    log.status === 'failed' ? 'bg-red-500/20' : 'bg-yellow-500/20'}`
                    }>
                        <ChannelIcon className={`h-4 w-4 ${
                      log.status === 'delivered' ? 'text-green-500' :
                      log.status === 'failed' ? 'text-red-500' : 'text-yellow-500'}`
                      } />
                      </div>
                      <div>
                        <p className="font-medium capitalize">{log.channel}</p>
                        <p className="text-sm text-muted-foreground">{log.recipient}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant={log.status === 'delivered' ? 'default' : log.status === 'failed' ? 'destructive' : 'secondary'}>
                        {log.status}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {log.timestamp ? new Date(log.timestamp).toLocaleTimeString() : 'Just now'}
                      </span>
                    </div>
                  </div>);

            })
            }
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid gap-4 md:grid-cols-4">
        <Link to="/communications/telegram" className="block">
          <Card className="hover:border-blue-500 transition-colors cursor-pointer h-full">
            <CardContent className="pt-6 flex flex-col items-center text-center">
              <Send className="h-8 w-8 text-blue-500 mb-3" />
              <h3 className="font-semibold">{t("telegram", "Telegram")}</h3>
              <p className="text-sm text-muted-foreground mt-1">{t("configureBotSettings", "Configure bot settings")}</p>
            </CardContent>
          </Card>
        </Link>
        <Link to="/communications/whatsapp" className="block">
          <Card className="hover:border-green-500 transition-colors cursor-pointer h-full">
            <CardContent className="pt-6 flex flex-col items-center text-center">
              <MessageSquare className="h-8 w-8 text-green-500 mb-3" />
              <h3 className="font-semibold">{t("whatsapp", "WhatsApp")}</h3>
              <p className="text-sm text-muted-foreground mt-1">{t("businessApiSetup", "Business API setup")}</p>
            </CardContent>
          </Card>
        </Link>
        <Link to="/communications/sms" className="block">
          <Card className="hover:border-orange-500 transition-colors cursor-pointer h-full">
            <CardContent className="pt-6 flex flex-col items-center text-center">
              <Phone className="h-8 w-8 text-orange-500 mb-3" />
              <h3 className="font-semibold">{t("sms", "SMS")}</h3>
              <p className="text-sm text-muted-foreground mt-1">{t("twilioIntegration", "Twilio integration")}</p>
            </CardContent>
          </Card>
        </Link>
        <Link to="/communications/email" className="block">
          <Card className="hover:border-violet-500 transition-colors cursor-pointer h-full">
            <CardContent className="pt-6 flex flex-col items-center text-center">
              <Mail className="h-8 w-8 text-violet-500 mb-3" />
              <h3 className="font-semibold">{t("email", "Email")}</h3>
              <p className="text-sm text-muted-foreground mt-1">{t("smtpConfiguration", "SMTP configuration")}</p>
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>);

};

export default CommunicationsDashboard;